<div class="container">
  <main>
    
    <div class="search-section mb-3">
      <div class="card shadow-sm">
        <div class="card-body">
          <div class="input-group">
            <span class="input-group-text">
              <i class="bi bi-search"></i>
            </span>
            <input type="text" [formControl]="searchControl" class="form-control" placeholder="Search by sentence"
              aria-label="Search words" />
            @if (searchControl.value) {
            <button class="btn btn-outline-secondary" type="button" (click)="clearSearch()" aria-label="Clear search">
              ✕
            </button>
            }
          </div>
        </div>
      </div>
    </div>

    
    @if (loading) {
    <div class="text-center my-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading...</p>
    </div>
    }

    
    @if (error) {
    <div class="alert alert-danger alert-dismissible fade show" role="alert">
      {{ error }}
      <button type="button" class="btn-close" (click)="error = undefined"></button>
    </div>
    }

    
    @if (!loading && words.length === 0) {
    <div class="alert alert-warning">
      @if (searchActive) {
      No words found matching "{{ searchControl.value }}".
      <button class="btn btn-link" (click)="clearSearch()">
        Clear search
      </button>
      } @else {
      No words found.
      }
    </div>
    }

    
    @if (!loading && words.length > 0) {
    <div class="table-responsive">
      <table class="table table-striped table-hover align-middle mb-0">
        <thead class="table-dark">
          <tr>
            <th role="button" style="cursor: pointer; user-select: none;" (click)="toggleSort()">
              Sentence
              <i class="bi ms-1" [class.bi-sort-alpha-down]="sortDir === 'asc'"
                [class.bi-sort-alpha-up]="sortDir === 'desc'" [class.bi-filter]="!sortDir"></i>
            </th>
            <th class="d-none d-sm-table-cell">Translation</th>
            <th class="d-none d-md-table-cell">Description</th>
            <th class="d-none d-sm-table-cell">Languages</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (word of words; track word.uuid) {
          <tr>
            <td>
              {{ word.sentence }}
              <div class="d-sm-none text-body-secondary small mt-1">{{ word.translation }}</div>
              @if (word.tags?.length) {
                <div class="d-none d-sm-flex flex-wrap gap-1 mt-1">
                  @for (tag of word.tags!; track tag.tag) {
                    <span class="badge rounded-pill bg-primary-subtle text-primary-emphasis">{{ tag.label }}</span>
                  }
                </div>
              }
            </td>
            <td class="d-none d-sm-table-cell text-body-secondary">{{ word.translation }}</td>
            <td class="d-none d-md-table-cell text-body-secondary small">{{ word.description }}</td>
            <td class="d-none d-sm-table-cell text-nowrap">
              {{ flags[word.language.name] }} {{ flags[word.languageTo.name] }}
            </td>
            <td class="text-end text-nowrap">
              <a [routerLink]="['/word', word.uuid]" class="btn btn-primary btn-sm px-2 me-1" title="View">
                <i class="bi bi-eye"></i>
              </a>
              <a [routerLink]="['/word/edit', word.uuid]" class="btn btn-warning btn-sm px-2 me-1" title="Edit">
                <i class="bi bi-pencil"></i>
              </a>
              <button class="btn btn-danger btn-sm px-2" (click)="onDelete(word)" title="Delete">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>
    }

    
    <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">

      
      <div class="form-check form-switch mb-0 me-1">
        <input class="form-check-input" type="checkbox" role="switch" id="filterMyLanguages"
          [(ngModel)]="filterMyLanguages" (change)="onLanguageFilterChange()" />
        <label class="form-check-label small" for="filterMyLanguages">My languages</label>
      </div>

      
      <div class="w-100 d-sm-none"></div>

      
      <span [appTooltip]="filterMyLanguages ? 'Uncheck My languages to edit' : ''">
        <select class="form-select form-select-sm" style="width: auto" [(ngModel)]="filterFromUuid"
          (change)="onLanguageDropdownChange()" [disabled]="filterMyLanguages">
          <option value="">Any</option>
          @for (l of availableLanguages; track l.uuid) {
          <option [value]="l.uuid">{{ flags[l.name] }} {{ l.name }}</option>
          }
        </select>
      </span>

      <i class="bi bi-arrow-right text-muted"></i>

      <span [appTooltip]="filterMyLanguages ? 'Uncheck My languages to edit' : ''">
        <select class="form-select form-select-sm" style="width: auto" [(ngModel)]="filterToUuid"
          (change)="onLanguageDropdownChange()" [disabled]="filterMyLanguages">
          <option value="">Any</option>
          @for (l of availableLanguages; track l.uuid) {
          <option [value]="l.uuid">{{ flags[l.name] }} {{ l.name }}</option>
          }
        </select>
      </span>

      
      @if (filterFromUuid || filterToUuid) {
      <button class="btn btn-sm btn-outline-secondary" type="button" (click)="swapLanguages()" title="Swap languages">
        <i class="bi bi-arrow-left-right"></i>
      </button>
      }

      @if (noLanguageSettings) {
      <small class="text-warning">
        <i class="bi bi-exclamation-triangle"></i>
        No language preferences set. <a routerLink="/settings/languages" class="text-warning">Configure them here.</a>
      </small>
      }

      <span class="flex-grow-1"></span>
      <a routerLink="/word/new-word" class="btn btn-primary btn-sm">
        <i class="bi bi-plus-lg me-1"></i>Add Word
      </a>
    </div>

    
    @if (page && !loading) {
    <nav class="mt-3">
      <ul class="pagination justify-content-center">
        <li class="page-item" [class.disabled]="page.page.number === 0">
          <button class="page-link" (click)="goToPage(page.page.number - 1)" [disabled]="page.page.number === 0">
            Previous
          </button>
        </li>
        <li class="page-item disabled">
          <span class="page-link">
            Page {{ page.page.number + 1 }} of {{ page.page.totalPages }}
          </span>
        </li>
        <li class="page-item" [class.disabled]="page.page.number >= page.page.totalPages - 1">
          <button class="page-link" (click)="goToPage(page.page.number + 1)"
            [disabled]="page.page.number >= page.page.totalPages - 1">
            Next
          </button>
        </li>
      </ul>
    </nav>
    }
  </main>
</div>